import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { useGetTelehealthSessionInfoCallback } from '../hooks';
import { useVideoPlatformCallback } from '../hooks/useVideoPlatform';
import { useTrackEvent } from '../segment/segment';
import { EventTypeEnum } from '../segment/types';
import { trySafely } from '../utils';
import { normalizeVideoPlatform, parseIdentity } from '../video-sdk';

export function useTrackSessionParticipantViewedEvent(participantIdentity: string | undefined) {
  const { patientShortId, providerShortId } = useParams<{
    patientShortId: string;
    providerShortId: string;
  }>();
  const [getTelehealthSessionInfo] = useGetTelehealthSessionInfoCallback();
  const getVideoPlatform = useVideoPlatformCallback();
  const { userViewed } = useTrackEvent();
  const hasLoggedRef = useRef(false);

  useEffect(
    function emitSessionParticipantViewed() {
      if (hasLoggedRef.current || !participantIdentity) return;
      hasLoggedRef.current = true;
      const resolvedPatientShortId = patientShortId ?? '';
      const resolvedProviderShortId = providerShortId ?? '';
      const { id: entityId, userType: entityType } = parseIdentity(participantIdentity);
      void (async () => {
        const { data } =
          (patientShortId && providerShortId
            ? await trySafely(
                getTelehealthSessionInfo({
                  variables: { patientShortId, providerShortId },
                }),
              )
            : {}) ?? {};
        userViewed(EventTypeEnum.SESSION_PARTICIPANT_VIEWED, {
          patientShortId: resolvedPatientShortId,
          providerShortId: resolvedProviderShortId,
          appointmentShortId: data?.telehealthSessionInfo?.appointment?.shortId ?? '',
          source: normalizeVideoPlatform(getVideoPlatform()) ?? '',
          entityId: entityId ?? '',
          entityType,
          // txId omitted: unable to get the admission transaction ID for other
          // participants
        });
      })();
    },
    [getTelehealthSessionInfo, getVideoPlatform, participantIdentity, patientShortId, providerShortId, userViewed],
  );
}
